import React, { useState } from 'react';
import { IconEye, IconEyeOff } from '@tabler/icons-react';
import { DashboardCard, User } from 'types';

interface DashboardCardsProps {
  profile: User;
}

const DashboardCards: React.FC<DashboardCardsProps> = ({ profile }) => {
  const cards: DashboardCard[] = [
    {
      title: 'Wallet balance',
      value: `₦${parseFloat(profile.wallet_balance).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`,
      showToggle: true,
      active: true,
    },
    {
      title: 'Balance in escrow',
      value: `₦${parseFloat(profile.escrow_balance).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`,
      showToggle: true,
    },
    {
      title: 'Pending orders',
      value: profile.pending_transactions,
    },
  ];

  const [toggles, setToggles] = useState(cards.map(() => true));
  const [activeIndex, setActiveIndex] = useState(0);

  const handleToggle = (idx: number) => {
    setToggles((prev) => prev.map((val, i) => (i === idx ? !val : val)));
  };

  return (
    <div>
      {/* Mobile: show one card at a time */}
      <div className="block md:hidden">
        <div className="w-full">
          {cards.map((card, index) => {
            if (index !== activeIndex) return null; // only show active

            const { title, value, active = false, showToggle = false } = card;
            const isVisible = showToggle ? toggles[index] : true;
            const displayValue = isVisible ? value : '****';

            return (
              <div
                key={index}
                className={`
                  ${active ? 'bg-primary-600' : 'bg-primary-50'}
                  shadow-sm rounded-lg p-7 flex flex-col justify-between space-y-9
                `}
              >
                <div
                  className={`text-sm flex items-center mb-2 font-normal ${
                    active ? 'text-white' : 'text-gray-500'
                  }`}
                >
                  {title}
                  {showToggle && (
                    <span onClick={() => handleToggle(index)}>
                      {toggles[index] ? (
                        <IconEye className="w-4 h-4 ml-1 cursor-pointer" />
                      ) : (
                        <IconEyeOff className="w-4 h-4 ml-1 cursor-pointer" />
                      )}
                    </span>
                  )}
                </div>

                <div
                  className={`text-3xl ${
                    active
                      ? 'font-semibold text-white'
                      : 'font-medium text-gray-900'
                  }`}
                >
                  {displayValue}
                </div>
              </div>
            );
          })}
        </div>

        {/* Navigation dots */}
        <div className="flex justify-center mt-4 space-x-2">
          {cards.map((_, index) => (
            <button
              key={index}
              onClick={() => setActiveIndex(index)}
              className={`w-2 h-2 rounded-full ${
                activeIndex === index ? 'bg-primary-600' : 'bg-gray-300'
              }`}
            />
          ))}
        </div>
      </div>

      {/* Desktop: grid view */}
      <div className="hidden md:grid md:grid-cols-3 md:gap-5 md:mb-8">
        {cards.map((card, index) => {
          const { title, value, active = false, showToggle = false } = card;
          const isVisible = showToggle ? toggles[index] : true;
          const displayValue = isVisible ? value : '****';

          return (
            <div
              key={index}
              className={`
                ${active ? 'bg-primary-600' : 'bg-primary-50'}
                shadow-sm rounded-lg p-7 flex flex-col justify-between space-y-9
              `}
            >
              <div
                className={`text-sm flex items-center mb-2 font-normal ${
                  active ? 'text-white' : 'text-gray-500'
                }`}
              >
                {title}
                {showToggle && (
                  <span onClick={() => handleToggle(index)}>
                    {toggles[index] ? (
                      <IconEye className="w-4 h-4 ml-1 cursor-pointer" />
                    ) : (
                      <IconEyeOff className="w-4 h-4 ml-1 cursor-pointer" />
                    )}
                  </span>
                )}
              </div>

              <div
                className={`text-3xl ${
                  active
                    ? 'font-semibold text-white'
                    : 'font-medium text-gray-900'
                }`}
              >
                {displayValue}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default DashboardCards;
